/**
 * 库存分配相关API接口
 */

import { unifiedApi } from '../../utils/unifiedApi';

// 库存分配状态
export enum AllocationStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  EXPIRED = 'expired'
}

// 分配类型
export enum AllocationType {
  PERCENTAGE = 'percentage', // 按百分比分配
  FIXED = 'fixed',          // 固定数量
  UNLIMITED = 'unlimited'   // 不限制数量
}

// 库存分配项
export interface StockAllocationItem {
  id: string;
  productId: string;
  productName: string;
  productSku: string;
  productImage: string;
  allocatedQuantity: number;
  allocationType: AllocationType;
  allocationValue: number;  // 百分比值或固定数量值
  minOrderQuantity: number; // 最小订购量
  priceMode?: 'discount' | 'fixed'; // 价格模式：折扣率或固定价
  discount?: number;         // 折扣率 0-100
  fixedPrice?: number;      // 固定价格
  remainingQuantity: number;
  soldQuantity: number;
  warehouseId?: string;
  warehouseName?: string;
}

// 库存分配
export interface StockAllocation {
  id: string;
  name: string;
  customerId: string;
  customerName: string;
  customerLevel: number;
  customerContact: string;
  status: AllocationStatus;
  effectiveDate: string;
  expiryDate: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  description: string;
  warehouseRule: 'all' | 'specific'; // all：所有仓库，specific：指定仓库
  items: StockAllocationItem[];
  totalItems: number;
  totalAllocatedQuantity: number;
}

/**
 * 获取库存分配列表
 */
export const getStockAllocationList = async (params?: any) => {
  return unifiedApi.service('stockAllocation').get<{
    data: StockAllocation[];
    count: number;
    page: number;
    pageSize: number;
  }>('/inventory/allocations', { params });
};

/**
 * 获取库存分配详情
 */
export const getStockAllocationDetail = async (id: string) => {
  return unifiedApi.service('stockAllocation').get<StockAllocation>(`/inventory/allocations/${id}`);
};

/**
 * 获取库存分配详情(通过ID)
 * 用于表单编辑
 */
export const getStockAllocationById = async (id: string) => {
  const response = await unifiedApi.service('stockAllocation').get<StockAllocation>(`/inventory/allocations/${id}`);
  return {
    success: true,
    data: response.data
  };
};

// 创建库存分配的表单数据
export interface StockAllocationFormData {
  name: string;
  customerId: string;
  customerName: string;
  customerLevel: number;
  customerContact: string;
  status: AllocationStatus;
  effectiveDate: string;
  expiryDate: string;
  description: string;
  warehouseRule: 'all' | 'specific';
  items: Omit<StockAllocationItem, 'id'>[];
}

/**
 * 创建库存分配
 */
export const createStockAllocation = async (data: StockAllocationFormData) => {
  return unifiedApi.service('stockAllocation').post<StockAllocation>('/inventory/allocations', data);
};

/**
 * 更新库存分配
 */
export const updateStockAllocation = async (id: string, data: Partial<StockAllocationFormData>) => {
  return unifiedApi.service('stockAllocation').put<StockAllocation>(`/inventory/allocations/${id}`, data);
};

/**
 * 删除库存分配
 */
export const deleteStockAllocation = async (id: string) => {
  return unifiedApi.service('stockAllocation').delete<{ success: boolean }>(`/inventory/allocations/${id}`);
};

/**
 * 批量删除库存分配
 */
export const batchDeleteStockAllocations = async (ids: string[]) => {
  return unifiedApi.service('stockAllocation').post<{ success: boolean; deleted_count: number }>('/inventory/allocations/batch/delete', { ids });
};

/**
 * 修改库存分配状态
 */
export const updateStockAllocationStatus = async (id: string, status: AllocationStatus) => {
  return unifiedApi.service('stockAllocation').put<{ id: string; status: AllocationStatus }>(`/inventory/allocations/${id}/status`, { status });
};

// 客户信息接口
export interface Customer {
  id: string;
  name: string;
  level: number;
  contact: string;
  phone: string;
  email: string;
  address: string;
  status: 'active' | 'inactive';
}

/**
 * 获取可分配的下级客户列表
 */
export const getSubCustomerList = async (params?: any) => {
  return unifiedApi.service('stockAllocation').get<{
    data: Customer[];
    count: number;
    page: number;
    pageSize: number;
  }>('/inventory/customers/sub', { params });
};

// 可分配的商品信息
export interface AllocatableProduct {
  id: string;
  name: string;
  sku: string;
  image: string;
  price: number;
  stock_quantity: number;
  warehouse_id?: string;
  warehouse_name?: string;
  category_id: string;
  category_name: string;
}

/**
 * 获取可分配的商品列表
 */
export const getAllocatableProducts = async (params?: any) => {
  return unifiedApi.service('stockAllocation').get<{
    data: AllocatableProduct[];
    count: number;
    page: number;
    pageSize: number;
  }>('/inventory/products/allocatable', { params });
};

// 仓库信息
export interface Warehouse {
  id: string;
  name: string;
  address: string;
  region: string;
  manager: string;
  contact: string;
  status: 'active' | 'inactive';
  capacity: number;
  used_capacity: number;
}

/**
 * 获取可分配的仓库列表
 */
export const getAllocatableWarehouses = async () => {
  return unifiedApi.service('stockAllocation').get<{
    data: Warehouse[];
    count: number;
  }>('/inventory/warehouses/allocatable');
};

// 库存分配可行性检查结果
export interface FeasibilityCheckResult {
  feasible: boolean;
  issues: {
    product_id: string;
    product_name: string;
    required_quantity: number;
    available_quantity: number;
    warehouse_id?: string;
  }[];
}

/**
 * 检查库存分配可行性
 */
export const checkAllocationFeasibility = async (data: {
  items: Array<{
    product_id: string;
    allocation_type: AllocationType;
    allocation_value: number;
    warehouse_id?: string;
  }>;
}) => {
  return unifiedApi.service('stockAllocation').post<FeasibilityCheckResult>('/inventory/allocations/check', data);
};

// 库存分配历史记录
export interface AllocationHistoryRecord {
  id: string;
  allocation_id: string;
  action: 'create' | 'update' | 'delete' | 'status_change';
  status_before?: AllocationStatus;
  status_after?: AllocationStatus;
  changes?: {
    field: string;
    before: any;
    after: any;
  }[];
  created_at: string;
  created_by: string;
  user_name: string;
  description: string;
}

/**
 * 获取分配历史记录
 */
export const getAllocationHistory = async (allocationId: string) => {
  return unifiedApi.service('stockAllocation').get<{
    data: AllocationHistoryRecord[];
    count: number;
  }>(`/inventory/allocations/${allocationId}/history`);
};
